import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface User {
  id: string;
  name: string;
  email: string;
  provider: string;
  role: string;
  vote_count: number;
  created_at: string;
}

export default function UserManager() {
  const { authToken } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const headers: Record<string, string> = {
    Authorization: `Bearer ${authToken}`,
    'Content-Type': 'application/json',
  };

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE}/admin/users`, { headers });
      if (!res.ok) throw new Error(`Failed to fetch users: ${res.status}`);
      const data = await res.json();
      setUsers(data.users || data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const toggleRole = async (user: User) => {
    const newRole = user.role === 'admin' ? 'user' : 'admin';
    setTogglingId(user.id);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${user.id}/role`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ role: newRole }),
      });
      if (!res.ok) throw new Error(`Failed to update role: ${res.status}`);
      setUsers((prev) =>
        prev.map((u) =>
          u.id === user.id ? { ...u, role: newRole } : u
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setTogglingId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-arena-accent border-t-transparent rounded-full animate-spin" />
        <span className="ml-3 text-arena-muted">Loading users...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg">
        {error}
        <button onClick={fetchUsers} className="ml-3 underline hover:no-underline">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-arena-muted">
          {users.length} user{users.length !== 1 ? 's' : ''} registered
        </p>
        <button onClick={fetchUsers} className="btn btn-secondary text-sm">
          Refresh
        </button>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-arena-border text-arena-muted text-sm">
                <th className="text-left p-3 font-medium">Name</th>
                <th className="text-left p-3 font-medium">Email</th>
                <th className="text-left p-3 font-medium">Provider</th>
                <th className="text-center p-3 font-medium">Role</th>
                <th className="text-right p-3 font-medium">Votes</th>
                <th className="text-right p-3 font-medium">Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-arena-muted">
                    No users found.
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr
                    key={user.id}
                    className="border-b border-arena-border last:border-0 hover:bg-arena-surface/50 transition-colors"
                  >
                    <td className="p-3">
                      <span className="font-medium text-arena-text">{user.name}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-arena-muted">{user.email}</span>
                    </td>
                    <td className="p-3">
                      <span className="inline-block px-2 py-0.5 text-xs rounded bg-arena-surface text-arena-muted capitalize">
                        {user.provider}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <button
                        onClick={() => toggleRole(user)}
                        disabled={togglingId === user.id}
                        className={`inline-block px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                          user.role === 'admin'
                            ? 'bg-arena-accent/20 text-arena-accent hover:bg-arena-accent/30'
                            : 'bg-arena-border text-arena-muted hover:bg-arena-surface'
                        } ${togglingId === user.id ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                      >
                        {togglingId === user.id ? '...' : user.role}
                      </button>
                    </td>
                    <td className="p-3 text-right">
                      <span className="font-mono text-sm">{user.vote_count}</span>
                    </td>
                    <td className="p-3 text-right">
                      <span className="text-sm text-arena-muted">{formatDate(user.created_at)}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
